import React, { useState, useEffect } from "react";
import { FolderOpen, ChevronRight, Github, Plus, RefreshCw, Search, Terminal, Trash2, X, ArchiveX } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuthenticatedApi } from '../hooks/useAuthenticatedApi';
import { navigateToLatestThread as navigateToLatestThreadUtil } from '../utils/threadNavigation';
import { apiEndpoints } from "../utils/api";
import { useAuth } from "../contexts/AuthContext";
import { HelpSystem, HelpButton, useHelp } from './tour';
import { useSettingsModal } from '../contexts/SettingsModalContext';
import { useReadOnlyMode } from '../hooks/useReadOnlyMode';
import NewProjectModal from './NewProjectModal';
import DeleteProjectModal from './modals/DeleteProjectModal';
import { useIsLocalhost } from '../store/environmentStore';

// Global workspace refresh registry
let globalWorkspaceRefresh = null;

/**
 * Trigger a global refresh of workspaces from anywhere in the app
 */
export const refreshWorkspaces = () => {
  if (globalWorkspaceRefresh) {
    globalWorkspaceRefresh();
  }
};

// ProjectScreenshot component that fetches fresh URLs when needed
const ProjectScreenshot = ({ project, fetchScreenshotUrl }) => {
  const [screenshotUrl, setScreenshotUrl] = useState(project.latest_screenshot_url);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    // Check if we have a screenshot but the URL might be expired
    if (project.latest_screenshot_timestamp && !project.latest_screenshot_url) {
      // URL is missing or potentially expired, fetch a fresh one
      setIsLoading(true);
      fetchScreenshotUrl(project.id).then(url => {
        if (url) {
          setScreenshotUrl(url);
        }
        setIsLoading(false);
      });
    }
  }, [project.id, project.latest_screenshot_timestamp, project.latest_screenshot_url, fetchScreenshotUrl]);

  const handleImageError = async (e) => {
    // If image fails to load, try fetching a fresh URL once
    if (!hasError && project.latest_screenshot_timestamp) {
      setHasError(true);
      const freshUrl = await fetchScreenshotUrl(project.id);
      if (freshUrl) {
        setScreenshotUrl(freshUrl);
      } else {
        // Hide the image if we can't get a fresh URL
        e.target.style.display = 'none';
      }
    } else {
      // Hide the image if we've already tried or there's no screenshot
      e.target.style.display = 'none';
    }
  };

  if (!project.latest_screenshot_timestamp || (!screenshotUrl && !isLoading)) {
    // No screenshot available, show white placeholder
    return (
      <div className="absolute inset-0 bg-white dark:bg-white" />
    );
  }

  return (
    <>
      {isLoading && (
        <div className="absolute inset-0 bg-white dark:bg-white flex items-center justify-center">
          <RefreshCw className="w-6 h-6 text-gray-400 animate-spin" />
        </div>
      )}
      {screenshotUrl && (
        <img 
          src={screenshotUrl} 
          alt={`Screenshot of ${project.name}`}
          className="absolute inset-0 w-full h-full object-cover"
          onError={handleImageError}
          style={{ display: isLoading ? 'none' : 'block' }}
        />
      )}
    </>
  );
};

// Custom scrollbar styles for ProjectSelectionPage
const projectSelectionScrollbarStyles = `
  /* WebKit browsers (Chrome, Safari, Edge) */
  .project-selection-scrollbar::-webkit-scrollbar {
    width: 8px;
  }

  .project-selection-scrollbar::-webkit-scrollbar-track {
    background: transparent;
    border-radius: 4px;
  }

  .project-selection-scrollbar::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.15);
    border-radius: 4px;
    transition: all 0.2s ease;
    border: 2px solid transparent;
    background-clip: content-box;
  }

  .project-selection-scrollbar::-webkit-scrollbar-thumb:hover {
    background-color: rgba(0, 0, 0, 0.25);
  }

  .dark .project-selection-scrollbar::-webkit-scrollbar-thumb {
    background-color: rgba(255, 255, 255, 0.15);
  }

  .dark .project-selection-scrollbar::-webkit-scrollbar-thumb:hover {
    background-color: rgba(255, 255, 255, 0.25);
  }

  /* Firefox */
  .project-selection-scrollbar {
    scrollbar-width: thin;
    scrollbar-color: rgba(0, 0, 0, 0.15) transparent;
  }

  .dark .project-selection-scrollbar {
    scrollbar-color: rgba(255, 255, 255, 0.15) transparent;
  }

  /* Smooth scrolling */
  .project-selection-scrollbar {
    scroll-behavior: smooth;
  }

  /* Hide scrollbar when not needed */
  .project-selection-scrollbar:not(:hover)::-webkit-scrollbar-thumb {
    background-color: rgba(0, 0, 0, 0.08);
  }

  .dark .project-selection-scrollbar:not(:hover)::-webkit-scrollbar-thumb {
    background-color: rgba(255, 255, 255, 0.08);
  }

  /* Ultra-thin scrollbar on hover for more Apple-like feel */
  .project-selection-scrollbar:hover::-webkit-scrollbar {
    width: 10px;
  }

  /* Recent projects list specific styling */
  .recent-projects-scrollbar::-webkit-scrollbar {
    width: 6px;
  }

  .recent-projects-scrollbar::-webkit-scrollbar-track {
    background: transparent;
    border-radius: 3px;
    margin: 4px 0;
  }

  .recent-projects-scrollbar::-webkit-scrollbar-thumb {
    background-color: rgba(0, 0, 0, 0.12);
    border-radius: 3px;
    transition: all 0.2s ease;
  }

  .recent-projects-scrollbar::-webkit-scrollbar-thumb:hover {
    background-color: rgba(0, 0, 0, 0.2);
  }

  .dark .recent-projects-scrollbar::-webkit-scrollbar-thumb {
    background-color: rgba(255, 255, 255, 0.12);
  }

  .dark .recent-projects-scrollbar::-webkit-scrollbar-thumb:hover {
    background-color: rgba(255, 255, 255, 0.2);
  }

  .recent-projects-scrollbar {
    scrollbar-width: thin;
    scrollbar-color: rgba(0, 0, 0, 0.12) transparent;
  }

  .dark .recent-projects-scrollbar {
    scrollbar-color: rgba(255, 255, 255, 0.12) transparent;
  }

  /* Main page scrollbar for full page scroll */
  .main-page-scrollbar::-webkit-scrollbar {
    width: 8px;
  }

  .main-page-scrollbar::-webkit-scrollbar-track {
    background: transparent;
    border-radius: 4px;
  }

  .main-page-scrollbar::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.1);
    border-radius: 4px;
    transition: all 0.2s ease;
    border: 2px solid transparent;
    background-clip: content-box;
  }

  .main-page-scrollbar::-webkit-scrollbar-thumb:hover {
    background-color: rgba(0, 0, 0, 0.18);
  }

  .dark .main-page-scrollbar::-webkit-scrollbar-thumb {
    background-color: rgba(255, 255, 255, 0.1);
  }

  .dark .main-page-scrollbar::-webkit-scrollbar-thumb:hover {
    background-color: rgba(255, 255, 255, 0.18);
  }

  .main-page-scrollbar {
    scrollbar-width: thin;
    scrollbar-color: rgba(0, 0, 0, 0.1) transparent;
  }

  .dark .main-page-scrollbar {
    scrollbar-color: rgba(255, 255, 255, 0.1) transparent;
  }

  /* Smooth scrolling for page */
  .main-page-scrollbar {
    scroll-behavior: smooth;
  }

  /* Hide main page scrollbar when not needed */
  .main-page-scrollbar:not(:hover)::-webkit-scrollbar-thumb {
    background-color: rgba(0, 0, 0, 0.06);
  }

  .dark .main-page-scrollbar:not(:hover)::-webkit-scrollbar-thumb {
    background-color: rgba(255, 255, 255, 0.06);
  }

  /* Ultra-thin main page scrollbar on hover */
  .main-page-scrollbar:hover::-webkit-scrollbar {
    width: 10px;
  }
`;

// Inject styles into the document head
const injectProjectSelectionScrollbarStyles = () => {
  if (typeof document !== 'undefined') {
    const existingStyle = document.getElementById('project-selection-scrollbar-styles');
    if (!existingStyle) {
      const style = document.createElement('style');
      style.id = 'project-selection-scrollbar-styles';
      style.textContent = projectSelectionScrollbarStyles;
      document.head.appendChild(style);
    }
  }
};

const ProjectSelectionPage = ({ onProjectSelect }) => {
  const { displayName } = useAuth();
  const authenticatedApi = useAuthenticatedApi();
  const { openAccountSettings } = useSettingsModal();
  const { isReadOnly } = useReadOnlyMode();
  
  // Inject scrollbar styles on component mount
  useEffect(() => {
    injectProjectSelectionScrollbarStyles();
  }, []);

  const [serverWorkspaces, setServerWorkspaces] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const isLocalhost = useIsLocalhost();
  const [filterQuery, setFilterQuery] = useState("");
  const [showNewProjectModal, setShowNewProjectModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Navigate to most recent thread for a workspace, creating one if none exist
  const navigateToLatestThread = async (workspaceId) =>
    navigateToLatestThreadUtil(authenticatedApi, navigate, workspaceId);


  // Fetch workspaces from API
  useEffect(() => {
    fetchWorkspaces();
  }, []);

  const fetchWorkspaces = async () => {
    setIsLoading(true);
    try {
      const workspaces = await authenticatedApi.get(apiEndpoints.workspaces);
      
      const formattedWorkspaces = workspaces.map(workspace => ({
        is_sandbox: workspace.is_sandbox,
        path: workspace.directory_path,
        lastOpened: new Date(workspace.updated_at).toLocaleString('en-US', { 
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        }),
        lastOpenedDate: new Date(workspace.updated_at), // Keep raw date for sorting
        id: workspace.id,
        name: workspace.name || workspace.directory_path.split('/').pop(),
        port: workspace.port,
        latest_screenshot_url: workspace.latest_screenshot_url,
        latest_screenshot_timestamp: workspace.latest_screenshot_timestamp,
        can_access: workspace.can_access ?? true,
        can_access_message: workspace.can_access_message,
        redirect_modal: workspace.redirect_modal,
        redirect_section: workspace.redirect_section,
        template_slug: workspace.template_slug,
      }));
      
      setServerWorkspaces(formattedWorkspaces);
    } catch (err) {
      console.error('Error fetching workspaces:', err);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Register global refresh function
  useEffect(() => {
    globalWorkspaceRefresh = fetchWorkspaces;
    return () => {
      globalWorkspaceRefresh = null;
    };
  }, []);

  // Fetch fresh screenshot URL for a workspace
  const fetchScreenshotUrl = async (workspaceId) => {
    try {
      const response = await authenticatedApi.get(`${apiEndpoints.workspaces}/${workspaceId}/screenshot-url`);
      return response.screenshot_url;
    } catch (err) {
      console.error('Error fetching screenshot URL:', err);
      return null;
    }
  };

  // Delete project functionality using shared modal
  const handleDeleteClick = (project, event) => {
    event.stopPropagation();
    setProjectToDelete(project);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async (projectId) => {
    setIsDeleting(true);

    try {
      await authenticatedApi.delete(`${apiEndpoints.workspaces}/${projectId}`);
      await new Promise(resolve => setTimeout(resolve, 500));
      await fetchWorkspaces(); // Refresh the projects list
      handleDeleteCancel();
    } catch (err) {
      console.error('Error deleting project:', err);
      window.alert('Failed to delete project. Please try again.');
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setProjectToDelete(null);
    setIsDeleting(false);
  };

  // Use only server workspaces, sorted by last opened date and filtered by search query
  const allProjects = React.useMemo(() => {
    let filteredWorkspaces = serverWorkspaces;
    
    // Apply filter if there's a search query
    if (filterQuery.trim()) {
      const query = filterQuery.toLowerCase();
      filteredWorkspaces = serverWorkspaces.filter(workspace => 
        workspace.name.toLowerCase().includes(query) ||
        workspace.path.toLowerCase().includes(query)
      );
    }
    
    return filteredWorkspaces.sort((a, b) => {
      // Sort by lastOpenedDate in descending order (most recent first)
      return b.lastOpenedDate.getTime() - a.lastOpenedDate.getTime();
    }).slice(0, 100);
  }, [serverWorkspaces, filterQuery]);

  const normalizePath = (path) => {
    return path.trim();
  };

  const createWorkspace = async (path) => {
    try {
      const normalizedPath = normalizePath(path);
      const workspaceData = await authenticatedApi.post(apiEndpoints.workspaces, { 
        directory_path: normalizedPath,
        name: normalizedPath.split('/').pop() || normalizedPath,
        is_sandbox: false
      });
      // Navigate directly to the most recent thread (or create one)
      if (workspaceData?.id) {
        await navigateToLatestThread(workspaceData.id);
      }
    } catch (err) {
      console.error('Error creating workspace:', err);
      window.alert(err instanceof Error ? err.message : 'Failed to create workspace');
    }
  };

  const handleOpenProject = async (path) => {
    if (!path || !path.trim()) {
      window.alert("Please enter a valid project path");
      return;
    }
    
    createWorkspace(path);
  };


  const selectRecentProject = (path, workspaceId = null) => {
    if (workspaceId) {
      const workspace = serverWorkspaces.find(w => w.id === workspaceId);
      if (workspace) {
        navigateToLatestThread(workspaceId);
        return;
      }
    }
    createWorkspace(path);
  };

  const handleBrowseProject = async () => {
    try {
      if (!window.showDirectoryPicker) {
        window.alert("Directory selection is not supported in your browser. Please type the full path manually.");
        return;
      }

      const directoryHandle = await window.showDirectoryPicker();
      const directoryName = directoryHandle.name;
      
      // Prompt user for the full path since browser security prevents getting it
      const fullPath = window.prompt(`Enter the full path for "${directoryName}":`, `~/Projects/${directoryName}`);
      
      if (fullPath) {
        handleOpenProject(fullPath);
      }
    } catch (err) {
      if (err.name !== "AbortError") {
        window.alert("Failed to select directory: " + err.message);
        console.error("Directory selection error:", err);
      }
    }
  };

  const handleCloneRepository = () => {
    const repoUrl = window.prompt("Enter Git repository URL:");
    if (repoUrl) {
      const repoName = repoUrl.split("/").pop()?.replace(".git", "") || "repo";
      const projectPath = `~/KolegaProjects/${repoName}`;
      
      // Show confirmation with the path where it will be cloned
      if (window.confirm(`Clone repository to: ${projectPath}?`)) {
        handleOpenProject(projectPath);
      }
    }
  };

  const handleCreateProject = () => {
    if (isReadOnly) return;
    setShowNewProjectModal(true);
  };

  return (
    <div className="h-full bg-white dark:bg-neutral-950 flex flex-col">
      
      {/* Scrollable content area */}
      <div className="flex-1 overflow-y-auto main-page-scrollbar">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Projects Container */}
        <div className="w-full">
          {/* Header with title and new project button */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-semibold text-neutral-900 dark:text-white">Your Projects</h2>
            <div className="flex items-center space-x-3">
              <button 
                onClick={() => navigate('/deleted-projects')}
                className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-neutral-900 text-neutral-500 dark:text-neutral-400 transition-colors"
                title="View deleted projects"
                data-tour="deleted-projects"
              >
                <ArchiveX size={18} />
              </button>
              <button 
                onClick={fetchWorkspaces}
                className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-neutral-900 text-neutral-500 dark:text-neutral-400 transition-colors"
                disabled={isLoading}
                data-tour="refresh-button"
              >
                <RefreshCw size={18} className={`transition-all ${isLoading ? "animate-spin" : ""}`} />
              </button>
              <button
                onClick={handleCreateProject}
                disabled={isReadOnly}
                className={`rounded-xl px-4 py-2.5 flex items-center font-medium transition-all shadow-sm ${
                  isReadOnly
                    ? 'bg-neutral-300 dark:bg-neutral-700 text-neutral-500 dark:text-neutral-400 cursor-not-allowed'
                    : 'bg-brand-action hover:bg-brand-action-hover text-brand-action-text'
                }`}
                data-tour="create-project"
                title={isReadOnly ? 'View-only mode: upgrade to create a new project' : undefined}
              >
                <Plus size={16} className="mr-1.5" />
                New Project
              </button>
            </div>
          </div>

          {/* Search filter */}
          <div className="relative mb-6">
            <div className="relative">
              <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                <Search size={18} className="text-neutral-400" />
              </div>
              <input
                type="text"
                placeholder="Search projects..."
                className="w-full max-w-md bg-slate-100 dark:bg-neutral-900 text-neutral-900 dark:text-white rounded-xl px-4 py-3 pl-11 pr-11 text-sm outline-none border border-slate-300 dark:border-neutral-800 focus:ring-2 focus:ring-brand-action/20 focus:border-brand-action transition-all placeholder:text-neutral-400"
                value={filterQuery}
                onChange={(e) => setFilterQuery(e.target.value)}
                autoFocus={false}
              />
              {filterQuery && (
                <button 
                  className="absolute inset-y-0 right-3 flex items-center text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-200 transition-colors"
                  onClick={() => setFilterQuery('')}
                  title="Clear search"
                >
                  <X size={18} />
                </button>
              )}
            </div>
            {filterQuery && (
              <div className="mt-2">
                <span className="text-sm text-neutral-500 dark:text-neutral-400">
                  {allProjects.length} result{allProjects.length !== 1 ? 's' : ''}
                </span>
              </div>
            )}
          </div>

          {/* Projects grid */}
          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <RefreshCw size={24} className="animate-spin mr-3 text-neutral-400" />
              <span className="text-neutral-500 dark:text-neutral-400">Loading projects...</span>
            </div>
          ) : allProjects.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {allProjects.map((project, index) => (
                <div
                  key={index}
                  onClick={() => project.can_access && selectRecentProject(project.path, project.id)}
                  className={`bg-slate-100 dark:bg-neutral-900 rounded-xl border border-slate-300 dark:border-neutral-800 overflow-hidden hover:shadow-lg hover:scale-[1.02] transition-all duration-200 ${
                    project.can_access ? 'cursor-pointer' : 'cursor-not-allowed'
                  } group relative`}
                >
                  {/* 16:9 Screenshot placeholder */}
                  <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                    <div className={project.can_access ? '' : 'opacity-50'}>
                      <ProjectScreenshot 
                        project={project}
                        fetchScreenshotUrl={fetchScreenshotUrl}
                      />
                    </div>
                    
                    {/* Access denied overlay (shown on hover) */}
                    {!project.can_access && project.can_access_message && (
                      <div className="absolute inset-0 bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center p-4">
                        {project.redirect_modal && project.redirect_section ? (
                          <div
                            onClick={(e) => {
                              e.stopPropagation();
                              openAccountSettings(project.redirect_section);
                            }}
                            className="text-center cursor-pointer hover:scale-105 transition-transform"
                          >
                            <p className="text-brand-primary text-sm font-semibold hover:underline">
                              {project.can_access_message} →
                            </p>
                          </div>
                        ) : (
                          <div className="text-center">
                            <p className="text-brand-primary text-sm font-semibold">
                              {project.can_access_message}
                            </p>
                          </div>
                        )
                        }
                      </div>
                    )}
                  </div>
                  
                  {/* Project info */}
                  <div className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-sm text-neutral-900 dark:text-white truncate group-hover:text-brand-action transition-colors">
                          {project.name}
                        </h3>
                        {!project.is_sandbox && (
                          <p className="text-xs text-neutral-500 dark:text-neutral-400 truncate mt-1">
                            {project.path}
                          </p>
                        )}
                        <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-2">
                          Last opened: {project.lastOpened}
                        </p>
                      </div>
                      <button
                        onClick={(e) => handleDeleteClick(project, e)}
                        className="flex-shrink-0 ml-2 p-1.5 text-neutral-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-all duration-200 opacity-0 group-hover:opacity-100"
                        title={`Delete ${project.name}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-slate-100 dark:bg-neutral-900 mb-4">
                <FolderOpen size={28} className="text-slate-400 dark:text-neutral-600" />
              </div>
              <h3 className="text-lg font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                {filterQuery ? 'No matching projects' : 'No projects yet'}
              </h3>
              <p className="text-sm text-neutral-500 dark:text-neutral-400 max-w-sm">
                {filterQuery 
                  ? 'Try a different search term' 
                  : 'Get started by creating a new project'}
              </p>
            </div>
          )}
        </div>
        </div>
      </div>
      
      {/* Help System */}
      <HelpSystem pageName="projectSelection" />
      
      <HelpButton pageName="projectSelection" />
      
      {/* New Project Modal */}
      <NewProjectModal 
        isOpen={showNewProjectModal}
        onClose={() => setShowNewProjectModal(false)}
      />
      
      {/* Delete Project Modal */}
      <DeleteProjectModal
        isOpen={showDeleteModal}
        project={projectToDelete}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        isDeleting={isDeleting}
      />
    </div>
  );
};

export default ProjectSelectionPage;